'use client';

/* 地址管理 */
import React, { useEffect, useRef, useState } from 'react';
// 三方库/框架
import { useTranslation } from '@/i18n/client';
import to from 'await-to-js';
// 公共方法
import { getSavedPickUpList } from '@/apis/address/address.api';
import { sendClickTrack as sendPayClickTrack } from '@/tracks/25281';
import { sendClickTrack } from '@/tracks/25422';
import { curShippingAddress } from '@/utils/addressRequestUtils';
import { queryGlobalCountriesData } from '@/utils/countryUtils';
// 接口
import { Address, Country } from '@/apis/address/address.interface';
// 组件
import UKDialog from '@/components/UKDialog';
import UKPageLoading from '@/components/UKPageLoading';
import UKTruncatedTooltip from '@/components/UKTruncatedTooltip/index';
import NewOrEditAddress from '@/components/address/AddressCreate';
import AddressSelectDrawer from '@/components/address/AddressSelect/AddressSelectDrawer';
import CloseIcon from '@mui/icons-material/Close';
import { Alert, Button, IconButton, Tab, Tabs, Typography } from '@mui/material';
import DeliveryList, { DeliveryListRef } from './components/DeliveryList';
import PickupList, { PickupListRef } from './components/PickupList';
// 资源
import VectorDown from '@/assets/icons/VectorDownBig.svg';
import AddressAdd from '@/assets/icons/address/AddressAdd.svg';
//枚举
import { ALL_ACTION_TYPE } from '@/apis/checkout/constants/constants';
import { PAGE_TYPE } from '@/constants/address';
// 样式
import styles from './index.module.scss';

interface DataItemProps {
  isPersonModal?: boolean; //个人中心地址入口
  initAddressData?: Address | null;
  defaultShippingType?: 'delivery' | 'pickup'; // 默认选中的配送类型 不传默认delivery
  loadCurrentOrder?: (params?: any) => void;
  hide?: boolean;
  footerSlot?: React.ReactElement;
  boxPadding?: string
}

const AddressManage: React.FC<DataItemProps> = ({
  isPersonModal,
  initAddressData,
  defaultShippingType,
  loadCurrentOrder,
  hide = false,
  footerSlot,
  boxPadding = '16px'
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(initAddressData ? false : true);
  const [curAddress, setCurAddress] = useState<Address | null>(initAddressData || null);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openPickupDrawer, setOpenPickupDrawer] = useState(false);
  const [availableTypes, setAvailableTypes] = useState<('delivery' | 'pickup')[]>(['delivery']);
  const [shippingTypeTab, setShippingTypeTab] = useState<'delivery' | 'pickup'>('delivery');
  const [pageLoading, setPageLoading] = useState<boolean>(false);
  const [currentCountryInfo, setCurrentCountryInfo] = useState<Country | null>(null);

  const deliveryListRef = useRef<DeliveryListRef>(null);
  const pickupListRef = useRef<PickupListRef>(null);

  useEffect(() => {
    if (!isPersonModal) {
      checkoutInitAddress(initAddressData as any);
    }
    handleShippingTypeInit();
  }, [isPersonModal, defaultShippingType]);

  const handleShippingTypeInit = async () => {
    // 仅个人中心涉及pickup
    const needPickup = isPersonModal;
    if (needPickup) {
      setPageLoading(true);

      const { currentCountry, currentCountryCode } = await queryGlobalCountriesData();
      setCurrentCountryInfo(currentCountry);
      // 业务规则-当前国家是否支持自提 优先级最高
      let hasPickup = currentCountry?.hasPickup;

      // 个人中心场景，当前国家不支持自提，需要判断是否有收藏的自提点
      if (!hasPickup) {
        const [, result] = await to(getSavedPickUpList({ countryCode: currentCountryCode }));
        const {
          validPickUps = [],
          otherCountryPickUps = [],
          unValidPickUps = [],
        } = result?.data || {};
        hasPickup = validPickUps?.length || otherCountryPickUps?.length || unValidPickUps?.length;
      }

      // 个人中心展示delivery+pickup 其余入口默认只展示delivery
      const validTypes = hasPickup
        ? ['delivery' as const, 'pickup' as const]
        : ['delivery' as const];
      setAvailableTypes(validTypes);

      const initialType =
        defaultShippingType && validTypes.includes(defaultShippingType as 'delivery' | 'pickup')
          ? (defaultShippingType as 'delivery' | 'pickup')
          : validTypes[0];
      setShippingTypeTab(initialType);

      setPageLoading(false);
    }
  };

  /**
   * 处理结算地址更新
   * @param type init-没有用户地址 首次创建  changeAddress-有用户地址 操作变更
   * @param initAddress 调用方传入初始地址
   */
  const checkoutAddressUpdate = async (
    type: (typeof ALL_ACTION_TYPE)[keyof typeof ALL_ACTION_TYPE],
    initAddress?: Address,
  ) => {
    const cur = initAddress || (await curShippingAddress());
    if (cur) {
      setCurAddress(cur as Address);
      if (loadCurrentOrder) {
        loadCurrentOrder({ addressId: cur?.addressId, type, address: cur });
      }
    }
    setIsLoading(false);
    // 结算地址：切换不同地址则需求关闭弹窗
    handleClose();
  };

  const checkoutInitAddress = (initAddress?: Address) => {
    checkoutAddressUpdate(ALL_ACTION_TYPE.INIT, initAddress);
  };

  const checkoutChangeAddress = () => {
    checkoutAddressUpdate(ALL_ACTION_TYPE.CHANGE_ADDRESS);
  };

  const handleClose = () => {
    setOpenCreateModal(false);
  };

  const handleShippingTypeTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setShippingTypeTab(newValue as 'delivery' | 'pickup');
  };

  const checkOutAddress = () => {
    return (
      <div className={styles.box} style={{padding:boxPadding}}>
        <div className={styles.title}>
          {t('checkout.billingAddress.deliveryAddress')}
          <span
            className={styles.go}
            onClick={() => {
              setOpenCreateModal(true);
              sendPayClickTrack.SettleAccounts_OrderNew_Address();
            }}
          >
            {t('address.change')}
            <VectorDown width={10} className={styles.svg} />
          </span>
        </div>
        <div className={styles.flex}>
          <UKTruncatedTooltip
            text={curAddress?.name || ''}
            tooltipStyles={styles.country}
            tooltipProps={{
              placement: 'top',
              arrow: true,
            }}
          />
          {/* <span className={styles.phone}>{curAddress?.areaCode ? "+" + curAddress?.areaCode : ""} {curAddress?.mobile}</span> */}
        </div>
        <UKTruncatedTooltip
          text={curAddress?.fullAddress}
          textLines={2}
          tooltipStyles={styles.address}
          tooltipProps={{
            placement: 'top',
            arrow: true,
          }}
        />
        {footerSlot}
      </div>
    );
  };

  if (hide) return;

  if (pageLoading) {
    return (
      <div className={styles.addressManageContainer}>
        <UKPageLoading />
      </div>
    );
  }

  return (
    <div className={styles.addressManageContainer} style={isPersonModal ? { width: 1000 } : {}}>
      {isPersonModal ? (
        <>
          <div className={styles.spaceBetweenLine}>
            {availableTypes.length > 1 ? (
              <Tabs
                className={styles.tabBar}
                value={shippingTypeTab}
                aria-label='address selection tabs'
                onChange={handleShippingTypeTabChange}
              >
                {availableTypes.includes('delivery') && (
                  <Tab
                    value='delivery'
                    label={<span className={styles.tabItem}>{t('common.delivery')}</span>}
                    style={{ ...(shippingTypeTab === 'delivery' ? { color: '#1A1A1A' } : {}) }}
                  />
                )}
                {availableTypes.includes('pickup') && (
                  <Tab
                    value='pickup'
                    label={<span className={styles.tabItem}>{t('common.pickup')}</span>}
                    style={{ ...(shippingTypeTab === 'pickup' ? { color: '#1A1A1A' } : {}) }}
                  />
                )}
              </Tabs>
            ) : (
              <div className={styles.addressTitle}>{t('address.addresses')}</div>
            )}

            {shippingTypeTab === 'delivery' && (
              <Button
                className={styles.addBtn}
                onClick={() => {
                  deliveryListRef?.current?.openCreateAddressModal();
                  sendClickTrack.NeworderReceiverList_Add({ from_page: isPersonModal ? '1' : '2' });
                }}
                // style={{ opacity: addressList && addressList?.length >= 30 ? 0.4 : 1 }}
                // disabled={addressList && addressList?.length >= 30 ? true : false}
              >
                <AddressAdd /> {t('address.addAddress')}
              </Button>
            )}

            {shippingTypeTab === 'pickup' && currentCountryInfo?.hasPickup && (
              <Button
                className={styles.addBtn}
                onClick={() => {
                  setOpenPickupDrawer(true);
                }}
              >
                <AddressAdd /> {t('address.addPickupLocation')}
              </Button>
            )}
          </div>

          <div className={styles.addrListWrap}>
            {shippingTypeTab === 'delivery' && (
              <DeliveryList ref={deliveryListRef} isPersonModal={true} />
            )}
            {shippingTypeTab === 'pickup' && <PickupList ref={pickupListRef} />}
          </div>
        </>
      ) : (
        <>
          {!isLoading && curAddress && checkOutAddress()}
          {!isLoading && !curAddress && (
            <>
              <Alert severity='warning' sx={{ marginBottom: 2 }}>
                {t('address.enterShippingAddress')}
              </Alert>
              <NewOrEditAddress
                getAddress={checkoutInitAddress}
                isModal={false}
                pageType={PAGE_TYPE.SHIPPING}
              />
            </>
          )}
        </>
      )}

      <UKDialog
        open={openCreateModal}
        onClose={handleClose}
        showCloseBtn={true}
        sx={{
          '& .MuiDialog-paper': {
            width: '880px', // 设置自定义宽度
            Height: '596px',
          },
        }}
      >
        <IconButton
          aria-label='close'
          onClick={handleClose}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
        <Typography>
          <DeliveryList isPersonModal={isPersonModal} getAddress={checkoutChangeAddress} />
        </Typography>
      </UKDialog>

      <AddressSelectDrawer
        availableShippingTypes={['pickup']}
        availablePickupTypes={['recommended']}
        openDrawer={openPickupDrawer}
        showManage={false}
        setOpenDrawer={(val) => {
          setOpenPickupDrawer(val);
          // 当抽屉关闭时，刷新 pickupList 数据
          if (val === false && pickupListRef.current) {
            pickupListRef.current.fetchAddressList();
          }
        }}
      />
    </div>
  );
};

export default AddressManage;
